import { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import type { ReportChartDataPoint } from "helpers/types";

export type ChartDataPoint = ReportChartDataPoint;

interface ChartDataModalProps {
  open: boolean;
  onClose: () => void;
  dataPoints: ChartDataPoint[];
  onChange: (dataPoints: ChartDataPoint[]) => void;
}

const ChartDataModal = ({
  open,
  onClose,
  dataPoints,
  onChange,
}: ChartDataModalProps) => {
  const [localDataPoints, setLocalDataPoints] =
    useState<ChartDataPoint[]>(dataPoints);
  const [newDate, setNewDate] = useState<string>("");
  const [newOptionTotal, setNewOptionTotal] = useState<string>("");
  const [newStockTotal, setNewStockTotal] = useState<string>("");
  const [dateError, setDateError] = useState<string>("");
  const [optionTotalError, setOptionTotalError] = useState<string>("");
  const [stockTotalError, setStockTotalError] = useState<string>("");

  const validateInputs = useCallback((): boolean => {
    let isValid = true;

    if (!newDate) {
      setDateError("Date is required");
      isValid = false;
    } else {
      setDateError("");
    }

    if (!newOptionTotal) {
      setOptionTotalError("Option total value is required");
      isValid = false;
    } else if (isNaN(parseFloat(newOptionTotal))) {
      setOptionTotalError("Please enter a valid number");
      isValid = false;
    } else {
      setOptionTotalError("");
    }

    if (!newStockTotal) {
      setStockTotalError("Stock total value is required");
      isValid = false;
    } else if (isNaN(parseFloat(newStockTotal))) {
      setStockTotalError("Please enter a valid number");
      isValid = false;
    } else {
      setStockTotalError("");
    }

    return isValid;
  }, [newDate, newOptionTotal, newStockTotal]);

  const handleAdd = useCallback(() => {
    if (!validateInputs()) {
      return;
    }

    const newDataPoint: ChartDataPoint = {
      date: newDate,
      option_total: parseFloat(newOptionTotal),
      stock_total: parseFloat(newStockTotal),
    };

    const updatedDataPoints = [...localDataPoints, newDataPoint].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    setLocalDataPoints(updatedDataPoints);
    setNewDate("");
    setNewOptionTotal("");
    setNewStockTotal("");
    setDateError("");
    setOptionTotalError("");
    setStockTotalError("");
  }, [
    newDate,
    newOptionTotal,
    newStockTotal,
    localDataPoints,
    validateInputs,
  ]);

  const handleRemove = useCallback(
    (index: number) => {
      const updatedDataPoints = localDataPoints.filter((_, i) => i !== index);
      setLocalDataPoints(updatedDataPoints);
    },
    [localDataPoints]
  );

  const handleSave = useCallback(() => {
    onChange(localDataPoints);
    onClose();
  }, [localDataPoints, onChange, onClose]);

  const handleCancel = useCallback(() => {
    setLocalDataPoints(dataPoints);
    setNewDate("");
    setNewOptionTotal("");
    setNewStockTotal("");
    setDateError("");
    setOptionTotalError("");
    setStockTotalError("");
    onClose();
  }, [dataPoints, onClose]);

  const handleClearAll = useCallback(() => {
    setLocalDataPoints([]);
  }, []);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter") {
        handleAdd();
      }
    },
    [handleAdd]
  );

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="chart-data-modal-title"
    >
      <DialogTitle id="chart-data-modal-title">Manage Chart Data</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "#666" }}>
            Add New Data Point
          </Typography>
          <Box display="flex" alignItems="flex-start" gap={2}>
            <TextField
              label="Date"
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              error={!!dateError}
              helperText={dateError}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
              inputProps={{
                "aria-label": "Select date for data point",
              }}
            />
            <TextField
              label="Option Total ($)"
              type="number"
              value={newOptionTotal}
              onChange={(e) => setNewOptionTotal(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              error={!!optionTotalError}
              helperText={optionTotalError}
              sx={{ flex: 1 }}
              inputProps={{
                "aria-label": "Enter option total value for data point",
              }}
            />
            <TextField
              label="Stock Total ($)"
              type="number"
              value={newStockTotal}
              onChange={(e) => setNewStockTotal(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              error={!!stockTotalError}
              helperText={stockTotalError}
              sx={{ flex: 1 }}
              inputProps={{
                "aria-label": "Enter stock total value for data point",
              }}
            />
            <IconButton
              color="primary"
              onClick={handleAdd}
              aria-label="Add data point"
              sx={{ mt: 0.5 }}
            >
              <AddIcon />
            </IconButton>
          </Box>
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1, color: "#666" }}>
          Current Data Points ({localDataPoints.length})
        </Typography>

        {localDataPoints.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: "#999", textAlign: "center", py: 3 }}
          >
            No data points added yet. Use the form above to add data.
          </Typography>
        ) : (
          <List
            sx={{
              maxHeight: 300,
              overflow: "auto",
              border: "1px solid #E0E0E0",
              borderRadius: 1,
            }}
          >
            {localDataPoints.map((point, index) => (
              <ListItem
                key={`${point.date}-${index}`}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label={`Remove data point from ${new Date(point.date).toLocaleDateString()}`}
                    onClick={() => handleRemove(index)}
                    tabIndex={0}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
                sx={{
                  "&:hover": { backgroundColor: "#f5f5f5" },
                  borderBottom: "1px solid #E0E0E0",
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <ListItemText
                  primary={new Date(point.date).toLocaleDateString()}
                  secondary={`Option: ${formatCurrency(point.option_total)} | Stock: ${formatCurrency(point.stock_total)}`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClearAll}
          variant="outlined"
          color="error"
          disabled={localDataPoints.length === 0}
          aria-label="Clear all data points"
        >
          Clear All
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleCancel} aria-label="Cancel changes">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          aria-label="Save data points"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChartDataModal;
